<template>
  <a
    v-if="webAdLink"
    :href="webAdLink"
    :target="isExternalLink ? '_blank' : ''"
    :rel="isExternalLink ? 'noopener noreferrer' : ''"
    :aria-label="webAd.label"
  >
    <div class="wc-web-ad wc-web-ad--fixed-size wc-webad-gradient"></div>
    <div
      class="wc-web-ad wc-web-ad--fixed-size position-relative user-select-none shadow-sm"
      :style="{
        'background-image': `url('${imageSrc}')`,
      }"
    />
    <font-awesome-icon
      v-if="!hasImg"
      class="wc-web-ad__fallback-img position-absolute text-muted font-size-5xl ml-n4 mt-n4"
      icon="camera"
      :title="t('noProductImageAvailable')"
    />
  </a>

  <div v-else>
    <div class="wc-web-ad wc-web-ad--fixed-size wc-webad-gradient"></div>
    <button
      class="wc-web-ad wc-web-ad--fixed-size btn position-relative user-select-none shadow-sm"
      :style="{
        'background-image': `url('${imageSrc}')`,
      }"
      :aria-label="webAd.label"
    >
      <font-awesome-icon
        v-if="!hasImg"
        class="wc-web-ad__fallback-img position-absolute text-muted font-size-5xl ml-n4 mt-n4"
        icon="camera"
        :title="t('noProductImageAvailable')"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUpdated, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WebAd from '@/models/entities/web-ad';
import { configurationKey } from '@/injections';
import useElementSize from '@/composables/useElementSize';

const regexUrlBeginsWithSlashSlashWithOptionalProtocol = /^(?:[a-z+]+:)?\/\//i;

const WEB_AD_FIXED_WIDTH_STYLE_NAME = '--web-ad-fixed-width';

const props = defineProps<{
  webAd: WebAd;
}>();

const { t } = useI18n();
const config = inject(configurationKey);
const { addClientWidthToStyles } = useElementSize();

const hasImg = computed(() => !!props.webAd.image);
const imageSrc = computed(() => (hasImg.value ? `${config.cdn}/api/img/${props.webAd.image}` : ''));
const webAdLink = computed(() => props.webAd.link);
const webAdLinkIsRelativeUrl = computed<boolean>(() => webAdLink.value?.includes(config.siteUrl));
const isExternalLink = computed<boolean>(
  () =>
    !webAdLinkIsRelativeUrl.value &&
    regexUrlBeginsWithSlashSlashWithOptionalProtocol.test(webAdLink.value),
);

const handleResize = () => addClientWidthToStyles(WEB_AD_FIXED_WIDTH_STYLE_NAME);

onMounted(() => {
  addClientWidthToStyles(WEB_AD_FIXED_WIDTH_STYLE_NAME);
  window.addEventListener('resize', handleResize);
});

onUpdated(() => {
  addClientWidthToStyles(WEB_AD_FIXED_WIDTH_STYLE_NAME);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-web-ad {
  background-color: var(--secondary-lighter-2);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: relative;
  border-radius: 0.5rem;
  border: none;
  padding: 0;
  height: 100%;
  width: 100%;

  &.wc-web-ad--fixed-size {
    width: 100%;
    height: 20.625rem;
    margin-top: 0.875rem;
  }

  @include media-breakpoint-down(sm) {
    &.wc-web-ad--fixed-size {
      width: 100%;
      height: 13.75rem;
      margin-top: 0.875rem;
      border-radius: 0;
    }

    &.wc-webad-gradient {
      background: linear-gradient(to bottom, transparent 85%);
    }
  }

  .wc-web-ad__fallback-img {
    top: 50%;
    left: 50%;
  }
}

.wc-webad-gradient {
  position: absolute;
  z-index: 2;
  background: linear-gradient(to bottom, transparent 88%);
}
</style>
